import React, { useState, useEffect, useRef, useCallback } from 'react'
import ReactDOM from 'react-dom'
import Portal from '../Portal'
import provinceLetters from './provinceLetters'
import Icon from '../Icon'
import isFunction from 'lodash/isFunction'
import copy from 'copy-to-clipboard'
import './index.scss'

/**
 * 车牌键盘
 */

const provinces = [...provinceLetters, { text: '确定', letter: '' }] // 省份
const specialsLetters = ['港', '澳', '学', '警', '领', '使'] // 特殊车牌
const letters = [1, 2, 3, 4, 5, 6, 7, 8, 9, 0, 'Q', 'W', 'E', 'R', 'T', 'Y', 'U', 'I', 'O', 'P', 'A', 'S', 'D', 'F', 'G', 'H', 'J', 'K', 'L', '', 'Z', 'X', 'C', 'V', 'B', 'N', 'M', '确定']

interface CarKeyboardProps {
    defaultCarNo?: string // 初始车牌号
    visible: boolean // 是否显示键盘
    editIndex?: number // 当前编辑的字符在车牌的位置 0...
    onEditIndexChange?: (index) => void // 修改车牌字符编辑位置
    onChange: (value, values, index) => void // 车牌号编辑的回调函数
    onCancel: (value, values, type?) => void // 取消编辑车牌的回调
    onConfirm?: (value, values) => void // 点击确认按钮之后的回调
    autoClose?: boolean // 输入8位车牌号码后是否自动关闭车牌键盘，默认自动关闭
    autoGetProvince?: boolean // 是否自动获取当前省份简称，默认否
    inputContain?: HTMLElement // 输入框对象，用来识别点击其它区域隐藏键盘，不传的话，只能点击确认或者输入7位8位才能隐藏键盘
}
const CarKeyboard: (React.FC<CarKeyboardProps> & { config: any, destroy: any }) = props => {
    const [editIndex, setEditIndex] = useState<number>(props.editIndex ? props.editIndex : (props.defaultCarNo?.length ? (props.defaultCarNo?.length === 8 ? 7 : props.defaultCarNo?.length) : 0)) // 当前编辑的车牌对应索引值
    const portalRef = useRef(null) // 弹窗节点
    const [visible, setVisible] = useState<boolean>(false) // 是否显示键盘
    const [carNoList, setCarNoList] = useState<string[]>(Array(8).fill('').map((item, index) => props.defaultCarNo?.length ? (props.defaultCarNo[index] || '') : '')) // 车牌列表
    const carNoListRef = useRef<string[]>(Array(8).fill('').map((item, index) => props.defaultCarNo?.length ? (props.defaultCarNo[index] || '') : ''))
   
    useEffect(() => {
        carNoListRef.current = carNoList
    },[carNoList])
    const eventHandle = useCallback((event) => {
        // 是否点击的组件容器元素或子元素，跟阻止事件冒泡目标一致
        const isChildNode = portalRef.current.el === event.target || portalRef.current.el.contains(event.target)
        // 是否车牌输入元素，如果不是，则点击会自动隐藏键盘
        const isInputNode = props.inputContain === event.target || props.inputContain?.contains(event.target)
        if (!isChildNode && !isInputNode && isFunction(props.onCancel)) {
            props.onCancel(carNoListRef?.current?.join(''), [...carNoListRef?.current])
        }
    }, [])

    // 点击键盘容器
    const handleClickKeyboard = useCallback((event) => {
        // 阻止事件冒泡
        event.nativeEvent.stopImmediatePropagation()
    }, [])

    // 选择编辑位置
    const handleChangeIndex = (index) => {
        const carNoLen = carNoList.filter(item => !!item).length
        if(index > carNoLen) {
            setEditIndex(carNoLen)
            if(isFunction(props.onEditIndexChange)) {
                props.onEditIndexChange(carNoLen)
            }
        }else {
            setEditIndex(index)
            if(isFunction(props.onEditIndexChange)) {
                props.onEditIndexChange(index)
            }
        }
    }

    // 切换省份
    const handleChangeProvince = (record) => {
        if(record.letter !== '') {
            const _carNoList = [...carNoList]
            _carNoList[0] = record.text
            setCarNoList(_carNoList)
            setEditIndex(1)
            if (isFunction(props.onChange)) {
                props.onChange(_carNoList.join(''), [..._carNoList], 1)
            }
        }else {
            if (isFunction(props.onConfirm)) {
                props.onConfirm(carNoList.join(''), [...carNoList])
            }
            if (isFunction(props.onCancel)) {
                props.onCancel(carNoList.join(''), [...carNoList], 1)
            }
        }
    }

    // 切换字母
    const handleChangeLetter = (value, index) => {
        if(editIndex === 1 && index < 10) {
            // 输入第二个字符，禁用数字键盘
            return
        }
        if(value === 'I' || value === 'O') {
            // 特殊字母不可选
            return
        }
        if(value === '') {
            // 删除
            let _editIndex = editIndex
            if(!carNoList[editIndex]) {
                _editIndex--
            }
            const _carNoList = [...carNoList.slice(0, _editIndex), ...carNoList.slice(_editIndex + 1), '']
            setCarNoList(_carNoList)
            setEditIndex(editIndex - 1)
            if (isFunction(props.onChange)) {
                props.onChange(_carNoList.join(''), [..._carNoList], editIndex - 1)
            }
            return
        }
        if(value === '确定') {
            // 确定
            if (isFunction(props.onConfirm)) {
                props.onConfirm(carNoList.join(''), [...carNoList])
            }
            return
        }

        const _carNoList = [...carNoList]
        _carNoList[editIndex] = value
        setCarNoList(_carNoList)
        if(editIndex < 7) {
            if (isFunction(props.onChange)) {
                props.onChange(_carNoList.join(''), [..._carNoList], editIndex + 1)
            }
            setEditIndex(editIndex + 1)
        }else if(_carNoList.join('').length === 8) {
            // 输入满8位
            if(isFunction(props.onChange)) {
                props.onChange(_carNoList.join(''), [..._carNoList], editIndex)
            }
            if (isFunction(props.onConfirm)) {
                props.onConfirm(_carNoList.join(''), [..._carNoList])
            }
        }
    }

    // 修改特殊车牌最后一位
    const handleChangeSpecials = (record, index) => {
        if(editIndex !== 7) {
            return
        }
        const _carNoList = [...carNoList]
        _carNoList[editIndex] = record
        setCarNoList(_carNoList)
        if(isFunction(props.onChange)) {
            props.onChange(_carNoList.join(''), [..._carNoList], editIndex)
        }
        if (isFunction(props.onConfirm)) {
            props.onConfirm(_carNoList.join(''), [..._carNoList])
        }
    }

    // 监听当前编辑车牌位置的变化
    useEffect(() => {
        if(typeof(props.editIndex) === 'number' && props.editIndex === 0 && props.editIndex < 8) {
            setEditIndex(props.editIndex)
        }
    }, [props.editIndex])

    useEffect(() => {
        document.addEventListener('click', eventHandle)
        return () => {
            document.removeEventListener('click', eventHandle)
        }
    }, [])

    return (
        <Portal ref={portalRef}>
            <div className={`mume-carkeyboard ${visible ? 'visible' : ''}`} onClick={handleClickKeyboard}>
                <div className='mume-carkeyboard-nos'>
                    {carNoList.map((item, index) => <span className={`mume-carkeyboard-nos-item ${index === editIndex ? 'edit' : ''} ${index === 7 ? 'specials' : ''}`} key={index} onClick={() => handleChangeIndex(index)}>{item}</span>)}
                </div>
                <div className='mume-carkeyboard-main'>
                    {editIndex === 0 && (
                        <div className='mume-carkeyboard-provinces'>
                            {provinces.map((item, index) => {
                                return (
                                    <div className='mume-carkeyboard-provinces-item' onClick={() => handleChangeProvince(item)} key={index}>
                                        <span data-letter={item.letter}>{item.text}</span>
                                    </div>
                                )
                            })}
                        </div>
                    )}
                    {editIndex > 0 && (
                        <div className='mume-carkeyboard-letters'>
                            <div className='mume-carkeyboard-specials'>
                                {specialsLetters.map((item, index) => {
                                    return (
                                        <div className={`mume-carkeyboard-specials-item ${editIndex !== 7 ? 'disabled' : ''}`} onClick={() => handleChangeSpecials(item, index)} key={index}>
                                            <span>{item}</span>
                                        </div>
                                    )
                                })}
                                <div className='mume-carkeyboard-clipboard'></div>
                            </div>
                            <div className='mume-carkeyboard-letters-list'>
                                {letters.map((item, index) => {
                                    return (
                                        <div className={`mume-carkeyboard-letters-item ${(item === 'I' || item === 'O') ? 'disabled' : ''} ${editIndex === 1 && index < 10 ? 'disabled' : ''}`} onClick={() => handleChangeLetter(item, index)} key={index}>
                                            <span>{index === 29 ? <Icon icon='&#xe723;' /> : item}</span>
                                        </div>
                                    )
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Portal>
    )
}

export default CarKeyboard

// 函数式调用车牌键盘
interface CarKeyboardConfigProps extends Omit<CarKeyboardProps, 'onCancel' | 'visible'> {
    onCancel: (value, values, type?) => void // 取消编辑车牌的回调

}
const carKeyboardConfig = (options: CarKeyboardConfigProps) => {
    let element = document.querySelector('.mume-carkeyboard-react-dom')
    if(!element) {
        element = document.createElement('div')
        element.classList.add('mume-carkeyboard-react-dom')
        document.body.appendChild(element)
    }else {
        ReactDOM.unmountComponentAtNode(element)
    }

    // 销毁
    const destroy = () => {
        if (!!element) {
            ReactDOM.unmountComponentAtNode(element)
            document.body.removeChild(element)
        }
    }

    // 取消
    const handleCancel = (value, values, type) => {
        if( !type ) {
            destroy()
        }
        !!options?.onCancel && options?.onCancel(value, values)
    }

    // 确认
    const handleConfirm = (value, values) => {
        if(isFunction(options.onConfirm)) {
            options.onConfirm(value, values)
            destroy()
        }
        !!options?.onCancel && options?.onCancel(value, values)
    }
    
    // 编辑
    const handleChange = (value, values, index) => {
        if(isFunction(options.onChange)) {
            options.onChange(value, values, index)
        }
    }

    ReactDOM.render(<CarKeyboard {...options} visible={true} onConfirm={handleConfirm} onCancel={handleCancel} onChange={handleChange} />, element)
}

CarKeyboard.config = carKeyboardConfig

// 销毁组件.
CarKeyboard.destroy = () => {
    let element = document.querySelector('.mume-carkeyboard-react-dom')
    if(!!element) {
        ReactDOM.unmountComponentAtNode(element)
        document.body.removeChild(element)
    }
}